import { useQuery } from '@tanstack/react-query'
import type {
  BurndownGranularity,
  IInsight,
  IInstrumentInsightsPanel,
  IInstrumentRecommendation,
  IInstrumentRecommendationsPanel,
  ILoiBurndownSeries,
  ILoiDashboardSummary,
  ILoiInsightsPanel,
  ILoiInstrumentCard,
  ILoiRecommendationsPanel,
  ILoiTeamNote,
  ILoiTopAnswerMatrix,
  LoiInstrumentStatusFilter,
  QuestionAnswerSummary,
} from '@brushfire/types'
import { apiClient } from '@/api/http/client'
import { queryKeys } from '@/api/query-keys'
import type { ApiResponse } from '@/api/http/types'
import { useSessionStore } from '@/stores/useSessionStore'
import { normalizeInstrumentInsightsResponse } from './instrument-insights-normalize'
import { normalizeInstrumentRecommendationsPanel } from './instrument-recommendations-normalize'

const POLL_MS = 30_000

function tenantKey(tenantId: string | null | undefined) {
  return tenantId ?? '_'
}

export type LoiDashboardSummary = ILoiDashboardSummary

export type LoiInstrumentCard = ILoiInstrumentCard & {
  questions: Array<{
    questionId: string
    text: string
    type: string
    required: boolean
    responseCount: number
    responseTarget: number | null
    progressPercent: number
    answerSummary: QuestionAnswerSummary | null
  }>
}

export type { LoiInstrumentStatusFilter } from '@brushfire/types'

export function useLoiDashboard(loiId: string, options?: { enabled?: boolean }) {
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated)

  return useQuery({
    queryKey: queryKeys.loiDashboard.summary(tenantKey(tenantId), loiId),
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<ILoiDashboardSummary>>(
        `/api/v1/loi-dashboard/${loiId}`,
      )
      if (data.data === undefined) {
        throw new Error('LOI dashboard response missing data payload')
      }
      return data.data
    },
    enabled: (options?.enabled ?? true) && isAuthenticated && !!loiId,
    refetchInterval: POLL_MS,
  })
}

export function useLoiInstruments(
  loiId: string,
  status: LoiInstrumentStatusFilter = 'all',
) {
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated)

  return useQuery({
    queryKey: queryKeys.loiDashboard.instruments(tenantKey(tenantId), loiId, status),
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<LoiInstrumentCard[]>>(
        `/api/v1/loi-dashboard/${loiId}/instruments`,
        { params: { status } },
      )
      const rows = data.data
      return Array.isArray(rows) ? rows : []
    },
    enabled: isAuthenticated && !!loiId,
    refetchInterval: POLL_MS,
  })
}

export function useLoiBurndown(loiId: string, granularity: BurndownGranularity) {
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated)

  return useQuery({
    queryKey: queryKeys.loiDashboard.burndown(tenantKey(tenantId), loiId, granularity),
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<ILoiBurndownSeries>>(
        `/api/v1/loi-dashboard/${loiId}/burndown`,
        { params: { granularity } },
      )
      if (data.data === undefined) {
        throw new Error('LOI burndown response missing data payload')
      }
      return data.data
    },
    enabled: isAuthenticated && !!loiId,
    refetchInterval: POLL_MS,
  })
}

export function useLoiTopAnswers(loiId: string) {
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated)

  return useQuery({
    queryKey: queryKeys.loiDashboard.topAnswers(tenantKey(tenantId), loiId),
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<ILoiTopAnswerMatrix>>(
        `/api/v1/loi-dashboard/${loiId}/top-answers`,
      )
      if (data.data === undefined) {
        throw new Error('LOI top answers response missing data payload')
      }
      return data.data
    },
    enabled: isAuthenticated && !!loiId,
    refetchInterval: POLL_MS,
  })
}

export function useLoiInsights(loiId: string) {
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated)

  return useQuery({
    queryKey: queryKeys.loiDashboard.insights(tenantKey(tenantId), loiId),
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<ILoiInsightsPanel>>(
        `/api/v1/loi-dashboard/${loiId}/insights`,
      )
      if (data.data === undefined) {
        throw new Error('LOI insights response missing data payload')
      }
      return data.data
    },
    enabled: isAuthenticated && !!loiId,
    refetchInterval: (query) =>
      query.state.data?.insightGenerationInProgress ? 5_000 : POLL_MS,
  })
}

export function useLoiRecommendations(loiId: string) {
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated)

  return useQuery({
    queryKey: queryKeys.loiDashboard.recommendations(tenantKey(tenantId), loiId),
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<ILoiRecommendationsPanel>>(
        `/api/v1/loi-dashboard/${loiId}/recommendations`,
      )
      if (data.data === undefined) {
        throw new Error('LOI recommendations response missing data payload')
      }
      return data.data
    },
    enabled: isAuthenticated && !!loiId,
    refetchInterval: (query) =>
      query.state.data?.recommendationGenerationInProgress ? 5_000 : POLL_MS,
  })
}

export function useLoiTeamNotes(loiId: string) {
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated)

  return useQuery({
    queryKey: queryKeys.loiDashboard.teamNotes(tenantKey(tenantId), loiId),
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<ILoiTeamNote[]>>(
        `/api/v1/loi-dashboard/${loiId}/team-notes`,
      )
      const rows = data.data
      return Array.isArray(rows) ? rows : []
    },
    enabled: isAuthenticated && !!loiId,
    refetchInterval: POLL_MS,
  })
}

export function useInstrumentInsights(campaignId: string, instrumentId: string) {
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated)

  return useQuery({
    queryKey: queryKeys.insights.instrument(tenantKey(tenantId), campaignId, instrumentId),
    queryFn: async () => {
      const { data } = await apiClient.get<
        ApiResponse<IInstrumentInsightsPanel | IInsight[] | null>
      >(`/api/v1/campaigns/${campaignId}/instruments/${instrumentId}/insights`)
      return normalizeInstrumentInsightsResponse(data.data, campaignId, instrumentId)
    },
    enabled: isAuthenticated && !!campaignId && !!instrumentId,
    refetchInterval: POLL_MS,
  })
}

export function useInstrumentRecommendation(
  campaignId: string,
  instrumentId: string,
  enabled = true,
) {
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated)

  return useQuery({
    queryKey: queryKeys.insights.recommendation(
      tenantKey(tenantId),
      campaignId,
      instrumentId,
    ),
    queryFn: async () => {
      const { data } = await apiClient.get<
        ApiResponse<IInstrumentRecommendationsPanel | IInstrumentRecommendation | null>
      >(`/api/v1/campaigns/${campaignId}/instruments/${instrumentId}/recommendation`)
      return normalizeInstrumentRecommendationsPanel(data.data)
    },
    enabled: enabled && isAuthenticated && !!campaignId && !!instrumentId,
    refetchInterval: (query) =>
      query.state.data == null && enabled ? 5_000 : POLL_MS,
  })
}
